import { Injectable } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { UserService } from './users.service';
import { AccessIdentifier, UserAccess } from '../constants/userAccess';
import { firstValueFrom } from 'rxjs';
import { UserRole } from '../constants/userRole';
import { ActiveOrganisationService } from './activeOrganisation.service';

@Injectable({
  providedIn: 'root'
})

export class UserPermissionInitializerService {

  private userPermissions: string[] = [];
  private _isLoaded = false;
  private _loadPromise?: Promise<void>;

  constructor(
    private permissionsService: NgxPermissionsService,
    private userService: UserService,
    private activeOrganisationService: ActiveOrganisationService
  ) { }

  async loadPermissions(forceRefresh = false): Promise<void> {
    if (this._isLoaded && !forceRefresh) return;
    if (!this._loadPromise || forceRefresh) {
      this._loadPromise = this.getUserPermissions();
    }

    return this._loadPromise;
  }

  async getPermissions(): Promise<string[]> {
    await this.loadPermissions(); // Ensures permissions are loaded
    return this.userPermissions;
  }

  resetPermissions(): void {
    this._isLoaded = false;
    this._loadPromise = undefined;
    this.userPermissions = [];
    this.permissionsService.flushPermissions();
  }

  async getUserPermissions(): Promise<void> {
    const userPermissions: any = await firstValueFrom(this.userService.getLoggedInUserDetails());
    this.userPermissions = userPermissions.data;
    this.userService.setLoggedInUserDetails(userPermissions?.data);

    if (userPermissions?.data?.organisationId) {
      await this.activeOrganisationService.setActiveOrganisationId(userPermissions.data.organisationId);
    }

    this.userPermissions = this.extractPermissions(this.userPermissions);
    this.permissionsService.loadPermissions(this.userPermissions);
    this._isLoaded = true;
  }

  extractPermissions(userPermissions: any): string[] {
    const userAccess: string[] = [];
    //User Role
    if (!userPermissions?.userAccessData) {
      userAccess.push(UserAccess.superAdmin);
    }
    if (userPermissions?.userTypeId === UserRole.NZMB_STAFF) {
      userAccess.push(UserAccess.nzmbStaff);
    } else if (userPermissions?.userTypeId === UserRole.EXPORTER) {
      userAccess.push(UserAccess.exporter);
    } else if (userPermissions?.userTypeId === UserRole.AUDITOR_QCVS) {
      userAccess.push(UserAccess.auditorQCVS);
    } else if (userPermissions?.userTypeId === UserRole.AUDITOR_PH) {
      userAccess.push(UserAccess.auditorPH);
    }
    //Exporter Manager
    if (userPermissions?.userAccessData?.isExporterManager) {
      userAccess.push(UserAccess.exporterManager);
    }
    //Functions
    if (userPermissions?.userAccessData?.allowCertificateRequest > AccessIdentifier.None) {
      userAccess.push(UserAccess.allowCertificateRequest);
    }
    if (userPermissions?.userAccessData?.allowQuotaApplication > AccessIdentifier.None) {
      userAccess.push(UserAccess.allowQuotaApplication);
    }
    if (userPermissions?.userAccessData?.allowHandback > AccessIdentifier.None) {
      userAccess.push(UserAccess.allowHandBack);
    }
    if (userPermissions?.userAccessData?.allowTransfer > AccessIdentifier.None) {
      userAccess.push(UserAccess.allowTransfer);
    }
    //Settings
    if (userPermissions?.userAccessData?.allowSystemSettings > AccessIdentifier.None) {
      userAccess.push(UserAccess.allowSystemSettings);
      if (userPermissions?.userAccessData?.allowSystemSettings >= AccessIdentifier.Basic)
        userAccess.push(UserAccess.isBasicAdmin);
    }
    //Settings Admin
    if (userPermissions?.userAccessData?.advancedSystemSettings) {
      userAccess.push(UserAccess.advancedSystemSettings);
    }
    //User Module
    if (userPermissions?.userAccessData?.allowSetupUsers > AccessIdentifier.None) {
      userAccess.push(UserAccess.hasUserSetup);
      if (userPermissions?.userAccessData?.allowSetupUsers >= AccessIdentifier.View)
        userAccess.push(UserAccess.viewUserSetup);
      if (userPermissions?.userAccessData?.allowSetupUsers >= AccessIdentifier.Edit)
        userAccess.push(UserAccess.editUserSetup);
    }
    //Organization Module
    if (userPermissions?.userAccessData?.allowSetupOrganisation > AccessIdentifier.None) {
      userAccess.push(UserAccess.hasOrganizationSetup);
      if (userPermissions?.userAccessData?.allowSetupOrganisation >= AccessIdentifier.View)
        userAccess.push(UserAccess.viewOrganizationSetup);
      if (userPermissions?.userAccessData?.allowSetupOrganisation >= AccessIdentifier.Edit)
        userAccess.push(UserAccess.editOrganizationSetup);
    }
    //Quota Module
    if (userPermissions?.userAccessData?.allowSetupQuota > AccessIdentifier.None) {
      userAccess.push(UserAccess.hasQuotaSetup);
      if (userPermissions?.userAccessData?.allowSetupQuota >= AccessIdentifier.View)
        userAccess.push(UserAccess.viewQuotaSetup);
      if (userPermissions?.userAccessData?.allowSetupQuota >= AccessIdentifier.Edit)
        userAccess.push(UserAccess.editQuotaSetup);
    }
    //Quota Allocator
    if (userPermissions?.userAccessData?.quotaAllocator > AccessIdentifier.None) {
      userAccess.push(UserAccess.hasQuotaAllocator);
      if (userPermissions?.userAccessData?.quotaAllocator >= AccessIdentifier.View)
        userAccess.push(UserAccess.viewQuotaAllocator);
      if (userPermissions?.userAccessData?.quotaAllocator >= AccessIdentifier.Edit)
        userAccess.push(UserAccess.editQuotaAllocator);
    }
    //Allow Certificate Issue Wgtn
    if (userPermissions?.userAccessData?.allowCertificateIssueWgtn >= AccessIdentifier.Yes) {
      userAccess.push(UserAccess.allowCertificateIssueWgtn);
      if (userPermissions?.userAccessData?.allowCertificateIssueWgtn >= AccessIdentifier.YesWithWarning)
        userAccess.push(UserAccess.allowCertificateIssueWgtnWithWarning);
    }
    // if (userPermissions?.userAccessData?.allowCertificateIssueWgtn >= AccessIdentifier.No) {
    //   userAccess.push(UserAccess.allowCertificateIssueWgtnWithNo);
    // }
    //Allow Certificate Issue Brs
    if (userPermissions?.userAccessData?.allowCertificateIssueBrs >= AccessIdentifier.Yes) {
      userAccess.push(UserAccess.allowCertificateIssueBrs);
      if (userPermissions?.userAccessData?.allowCertificateIssueBrs >= AccessIdentifier.YesWithWarning)
        userAccess.push(UserAccess.allowCertificateIssueBrsWithWarning);
    }
    // if (userPermissions?.userAccessData?.allowCertificateIssueBrs >= AccessIdentifier.No) {
    //   userAccess.push(UserAccess.allowCertificateIssueWgtnWithNo);
    // }
    //Ecertissue
    if (userPermissions?.userAccessData?.ecertissue >= AccessIdentifier.SiteSpecified) {
      userAccess.push(UserAccess.allowEcertissueSiteSpecified);
      if (userPermissions?.userAccessData?.ecertissue >= AccessIdentifier.NotSpecified)
        userAccess.push(UserAccess.allowEcertissueNotSpecified);
      if (userPermissions?.userAccessData?.ecertissue >= AccessIdentifier.All) {
        userAccess.push(UserAccess.allowEcertissueAll);
      }
    }
    //Allow Setup Fees
    if (userPermissions?.userAccessData?.allowSetupFees > AccessIdentifier.None) {
      userAccess.push(UserAccess.hasFeesSetup);
      if (userPermissions?.userAccessData?.allowSetupFees >= AccessIdentifier.View)
        userAccess.push(UserAccess.viewFeesSetup);
      if (userPermissions?.userAccessData?.allowSetupFees >= AccessIdentifier.Edit)
        userAccess.push(UserAccess.editFeesSetup);
    }
    //Voyages
    if (userPermissions?.userAccessData?.voyages > AccessIdentifier.None) {
      userAccess.push(UserAccess.hasVoyages);
      if (userPermissions?.userAccessData?.voyages >= AccessIdentifier.View)
        userAccess.push(UserAccess.viewVoyages);
      if (userPermissions?.userAccessData?.voyages >= AccessIdentifier.Edit)
        userAccess.push(UserAccess.editVoyages);
    }
    //Importers
    if (userPermissions?.userAccessData?.importers > AccessIdentifier.None) {
      userAccess.push(UserAccess.hasImporters);
      if (userPermissions?.userAccessData?.importers >= AccessIdentifier.View)
        userAccess.push(UserAccess.viewImporters);
      if (userPermissions?.userAccessData?.importers >= AccessIdentifier.Edit)
        userAccess.push(UserAccess.editImporters);
    }
    //Prdoduction History
    if (userPermissions?.userAccessData?.productionHistory > AccessIdentifier.None) {
      userAccess.push(UserAccess.hasProductionHistory);
      if (userPermissions?.userAccessData?.productionHistory >= AccessIdentifier.View)
        userAccess.push(UserAccess.viewProductionHistory);
      if (userPermissions?.userAccessData?.productionHistory >= AccessIdentifier.Edit)
        userAccess.push(UserAccess.editProductionHistory);
    }
    //Exporter History
    if (userPermissions?.userAccessData?.exportHistory > AccessIdentifier.None) {
      userAccess.push(UserAccess.hasExporterHistory);
      if (userPermissions?.userAccessData?.exportHistory >= AccessIdentifier.View)
        userAccess.push(UserAccess.viewExporterHistory);
      if (userPermissions?.userAccessData?.exportHistory >= AccessIdentifier.Edit)
        userAccess.push(UserAccess.editExporterHistory);
    }
    //Audit
    if (userPermissions?.userAccessData?.plantAudit > AccessIdentifier.None) {
      userAccess.push(UserAccess.hasAudit);
      if (userPermissions?.userAccessData?.plantAudit >= AccessIdentifier.View)
        userAccess.push(UserAccess.viewAudit);
      if (userPermissions?.userAccessData?.plantAudit >= AccessIdentifier.Edit)
        userAccess.push(UserAccess.editAudit);
    }
    //Communication
    if (userPermissions?.userAccessData?.communications > AccessIdentifier.None) {
      userAccess.push(UserAccess.hasCommunication);
      if (userPermissions?.userAccessData?.communications >= AccessIdentifier.View)
        userAccess.push(UserAccess.viewCommunication);
      if (userPermissions?.userAccessData?.communications >= AccessIdentifier.Edit)
        userAccess.push(UserAccess.editCommunication);
    }
    //Reports
    if (userPermissions?.userAccessData?.reports > AccessIdentifier.None) {
      userAccess.push(UserAccess.hasReports);
      if (userPermissions?.userAccessData?.reports >= AccessIdentifier.View)
        userAccess.push(UserAccess.viewReports);
      if (userPermissions?.userAccessData?.reports >= AccessIdentifier.Edit)
        userAccess.push(UserAccess.editReports);
    }
    return userAccess;
  }
}

